/**
 * PersonaEditorPage — Brief 4 (Gestor) · Tela 2 Personas (PR1 · Foundation).
 *
 * Rota: /admin/ia/personas. Modo leitura sobre o CRUD real de Personas.
 * Padrão D-B (defense-in-depth): gate próprio na página (`persona-read`), além
 * da query gateada no hook — não confia só na rota/nav.
 *
 * Escopo PR1: lista de personas → editor shell (seções do gabarito) → seção
 * Identidade em leitura. Demais seções de leitura, edição (gate `persona-author`)
 * e ciclo de vida chegam em PRs seguintes.
 */

import { CapabilityLockedBanner } from '@lume/ui-components';
import { useState } from 'react';

import { PersonaEditorView } from '../features/personas/PersonaEditorView';
import { PersonaListView } from '../features/personas/PersonaListView';
import { usePersona, usePersonaList } from '../features/personas/use-personas';
import { useCapability } from '../hooks/use-capability';

import '../features/personas/personas-4.css';

export function PersonaEditorPage(): JSX.Element {
  const canRead = useCapability('persona-read');

  // D-B — gate próprio, não confiar só na rota protegida.
  if (!canRead) {
    return (
      <div className="personas-screen personas-screen--gate">
        <CapabilityLockedBanner
          capability="persona-read"
          tierMin="professional"
          fallbackMessage="A configuração de personas é restrita a perfis de gestão — fale com o administrador do tenant."
        />
      </div>
    );
  }

  return <PersonaEditorPageInner />;
}

function PersonaEditorPageInner(): JSX.Element {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const list = usePersonaList();
  const detail = usePersona(selectedId);

  if (selectedId !== null) {
    if (detail.isLoading) {
      return (
        <div className="personas-screen">
          <div className="pe-panel">
            <p className="pe-panel__subtitle">Carregando persona…</p>
          </div>
        </div>
      );
    }
    if (detail.isError || detail.data === undefined) {
      return (
        <div className="personas-screen">
          <div className="pe-panel">
            <p className="pe-alert" role="alert">
              Não foi possível carregar esta persona.
            </p>
            <button type="button" className="pe-btn" onClick={() => setSelectedId(null)}>
              Voltar à lista
            </button>
          </div>
        </div>
      );
    }
    return <PersonaEditorView persona={detail.data} onBack={() => setSelectedId(null)} />;
  }

  if (list.isLoading) {
    return (
      <div className="personas-screen">
        <div className="pe-panel">
          <p className="pe-panel__subtitle">Carregando personas…</p>
        </div>
      </div>
    );
  }
  if (list.isError) {
    return (
      <div className="personas-screen">
        <div className="pe-panel">
          <p className="pe-alert" role="alert">
            Falha ao carregar as personas.
          </p>
        </div>
      </div>
    );
  }

  return <PersonaListView personas={list.data} onOpen={(id) => setSelectedId(id)} />;
}
